// HotpleDetailPage.js
import React, { useEffect, useState, useRef } from 'react';
import LazyLoad from 'react-lazyload';
import styles from './HotpleDetailPage.module.css';
import axios from 'axios';
import { haversineDistance, openInstagramMedia, shareContent } from '../../util';
import HotpleHomeTab from './Home/HotpleHomeTab';

const ANIM_MS = 260;            // .sheet transition과 동일
const CLOSE_THRESHOLD = 80;      // 이 거리 이상 드래그하면 닫기

const HotpleDetailPage = ({
  isOpen,
  onClose,
  hotple,
  hotThreshold,
  marginTop,
  devicePosition,
  updateHotpleInfo,
  setHotpleModalPage,
  setModalProps,
  liked,
  likeCount
}) => {
  // ---- Hooks ----
  const [activeTab, setActiveTab] = useState('home');
  const [analysis, setAnalysis] = useState(null);
  const [hotpleSnapshot, setHotpleSnapshot] = useState(null);
  const hp = hotpleSnapshot || hotple;

  const LikeButton = React.memo(function LikeButton({ liked, count, hasPeriod, onClick }) {
    return (
      <div className={liked ? styles.mapButtonActive : styles.mapButton} onClick={onClick}>
        {liked ? (
          <img className={styles.buttonIconActive} alt="" src={hasPeriod ? 'images/notification_active.svg' : 'images/like_active.svg'} />
        ) : (
          <img className={styles.buttonIcon} alt="" src={hasPeriod ? 'images/notification.svg' : 'images/like_outlined.svg'} />
        )}
        <div>{count}</div>
      </div>
    );
  });

  const upperRef    = useRef(null);
  const sentinelRef = useRef(null);
  const tabRef      = useRef(null);
  const sheetRef    = useRef(null);
  const bodyRef     = useRef(null);

  // 언마운트 지연: 트랜지션을 위해 DOM은 유지
  const [shouldRender, setShouldRender] = useState(false);
  // 실제로 .open 클래스를 붙일지의 단계 (두 프레임 렌더)
  const [openStage, setOpenStage] = useState(false);

  // 드래그 상태
  const [dragOffset, setDragOffset] = useState(0);
  const dragging = useRef(false);
  const dragStartY = useRef(0);
  const dragOffsetRef = useRef(0); // onTouchEnd에서 최신 offset 참조용

  const getCachedHotpleData = (hotpleId) =>
    axios.get('https://hotple.today/win-api/api/analysis-naver', { params: { hotple_id: hotpleId } });

  // sticky shadow
  useEffect(() => {
    if (!sentinelRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        const isSticky = !entry.isIntersecting;
        tabRef.current?.classList.toggle(styles.shadow, isSticky);
        upperRef.current?.classList.toggle(styles.shadow, !isSticky);
      },
      { threshold: 0 }
    );
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, []);

  // body 스크롤 잠금(배경만)
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  // 열림/닫힘 애니메이션 + 스냅샷 갱신
  useEffect(() => {
    if (isOpen) {
      if (hotple) setHotpleSnapshot(hotple);
      setShouldRender(true);
      setDragOffset(0);
      dragOffsetRef.current = 0;
      setOpenStage(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setOpenStage(true));
      });
    } else {
      setOpenStage(false);
      const t = setTimeout(() => setShouldRender(false), ANIM_MS);
      return () => clearTimeout(t);
    }
  }, [isOpen, hotple?.hotple_id]);

  // ESC 닫기
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // 분석 캐시 로드
  useEffect(() => {
    const id = (hotpleSnapshot || hotple)?.hotple_id;
    if (!id) return;
    setAnalysis(null);
    if(hotple?.hotple_id)
      tryToGetCache(id);
  }, [hotpleSnapshot?.hotple_id, hotple?.hotple_id]);

  async function tryToGetCache(hotpleId) {
    try {
      const res = await getCachedHotpleData(hotpleId);
      const c = res?.data?.hotpleData;
      if (c) {
        setAnalysis({
          visitorReviewsCount: c.visitorReviewsCount,
          blogTextsCount: c.blogTextsCount,
          placeAnalysis: c.placeAnalysis,
          reviewAnalysis: c.reviewAnalysis,
          info: c.info
        });
      } else {
        setAnalysis(null);
      }
    } catch {
      setAnalysis(null);
    }
  }

  // 드래그 시작 가능 여부: (시작 시점에) 스크롤 최상단이거나 헤더인 경우만
  const canStartDrag = (targetEl) => {
    if (!bodyRef.current) return true;
    const atTop = bodyRef.current.scrollTop <= 0.5;
    const isHeader = upperRef.current && (upperRef.current === targetEl || upperRef.current.contains(targetEl));
    return atTop || isHeader;
  };

  // --- Touch Handlers (간결 버전: 내부 스크롤 잠금/해제, preventDefault 모두 제거) ---
  const onTouchStartSheet = (e) => {
    if (!openStage) return;
    const touch = e.touches?.[0]; if (!touch) return;
    if (!canStartDrag(e.target)) return;

    dragging.current = true;
    dragStartY.current = touch.clientY;
    sheetRef.current?.classList.add(styles.dragging);
  };

  const onTouchMoveSheet = (e) => {
    if (!dragging.current) return;
    const touch = e.touches?.[0]; if (!touch) return;

    const dy = Math.max(0, touch.clientY - dragStartY.current);
    setDragOffset(dy);
    dragOffsetRef.current = dy;
    // 내부 스크롤을 막지 않습니다. (UX 단순화)
  };

  const onTouchEndSheet = () => {
    if (!dragging.current) return;
    dragging.current = false;
    sheetRef.current?.classList.remove(styles.dragging);

    const dy = dragOffsetRef.current;
    dragOffsetRef.current = 0;

    if (dy > CLOSE_THRESHOLD) {
      requestClose();
    } else {
      setDragOffset(0);
    }
  };

  const isDragging = dragOffset > 0;

  const requestClose = () => {
    // 드래그 잔여 transform 제거 후 다음 프레임에 onClose
    if (dragOffset > 0) {
      setDragOffset(0);
      sheetRef.current?.classList.remove(styles.dragging);
      requestAnimationFrame(() => onClose?.());
    } else {
      onClose?.();
    }
  };

  // 가드: 열려있는 동안만 렌더
  if (!shouldRender) return null;
  if (!hp) return null;

  const { start_date, end_date, mention_count, created_at, has_comment_event, uploaded_at } = hp;

  let badge = null;
  if (has_comment_event && uploaded_at && (new Date(uploaded_at) >= new Date(Date.now() - 7 * 24 * 60 * 60 * 1000))) {
    badge = <div className={styles.periodBadge}>댓글 이벤트</div>;
  } else if (start_date && new Date(start_date) > new Date()) {
    badge = <div className={styles.endBadge}>시작예정</div>;
  } else if (end_date && new Date(end_date) < new Date()) {
    badge = <div className={styles.endBadge}>종료됨</div>;
  } else if (end_date && new Date(end_date) < new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)) {
    badge = <div className={styles.periodBadge}>종료임박</div>;
  } else if (mention_count > hotThreshold) {
    badge = <div className={styles.hotBadge}>HOT</div>;
  } else if (new Date(created_at) >= new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)) {
    badge = <div className={styles.newBadge}>NEW</div>;
  }

  const whenShareButtonClicked = async () => {
    await shareContent(
      'HOTPLE 공유',
      `[HOTPLE 공유]\n지금 인스타에서 핫한 '${hp.name}'\n이런 곳은 어때요? 😉\n\nhttps://app.hotple.today?menu=search&hotple_id=${hp.hotple_id}`
    );
  };
  const whenNaverButtonClicked = () => {
    const naverMapWebUrl = `https://map.naver.com/v5/entry/place/${hp.naver_map_place_id}`;
    window.open(naverMapWebUrl, '_blank');
  };
  const patchIsPick = () => {
    updateHotpleInfo('updateIsPick', hp);
  };

  return (
    <>
      {/* 오버레이 */}
      <div
        className={`${styles.sheetOverlay} ${openStage ? styles.open : ''}`}
        onClick={requestClose}
        aria-hidden
      />

      {/* 바텀 시트 */}
      <div
        ref={sheetRef}
        className={`${styles.sheet} ${openStage ? styles.open : ''} ${isDragging ? styles.dragging : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="장소 상세"
        onClick={(e) => e.stopPropagation()}
        style={{
          '--sheet-top-gap': `${marginTop + 16}px`,
          ...(isDragging ? { transform: `translateY(${dragOffset}px)` } : {})
        }}
        onTouchStart={onTouchStartSheet}
        onTouchMove={onTouchMoveSheet}
        onTouchEnd={onTouchEndSheet}
        onTouchCancel={onTouchEndSheet}
      >
        {/* 헤더 */}
        <div className={styles.sheetHeader} ref={upperRef}>
          <div className={styles.sheetTitle}>{hp?.name ?? ''}</div>
          <button className={styles.sheetClose} onClick={requestClose}>닫기</button>
        </div>

        {/* 바디 */}
        <div className={styles.sheetBody}>
          <div
            ref={bodyRef}
            className={styles.lowerframe}
            style={{ height: `calc(100dvh - var(--sheet-top-gap) - 61px)` }}
          >
            <div className={styles.contentframe}>
              <div className={styles.hotpleItemFrame}>
                <div className={styles.imageframe}>
                  <div className={styles.padding} />

                  {hp.contents_url && hp.contents_url[0] &&
                  hp.contents_url[0].includes('mp4') ? (
                    <div className={styles.image1frame} onClick={() => openInstagramMedia(hp.media_url)}>
                      <LazyLoad height={152} offset={333} once scrollContainer="#hotpleitems-container">
                        <img className={styles.image1Icon} alt="" src={hp.preview_url || 'images/no_instagram_image.svg'} />
                      </LazyLoad>
                      {hp.media_id && (
                        <div className={styles.infoframe}>
                          {hp.media_id?.[0]?.startsWith('H')
                            ? <img className={styles.iconfire2} alt="" src="images/fire.svg" />
                            : <img className={styles.iconinstagramColor} alt="" src="images/instagram.svg" />}
                          <div className={styles.username}>@{hp.media_creator}</div>
                        </div>
                      )}
                    </div>
                  ) : (
                    hp.contents_url?.map((url, index) => (
                      <div
                        key={index}
                        className={index === 0 ? styles.image1frame : styles.image2frame}
                        onClick={() => openInstagramMedia(hp.media_url)}
                      >
                        <LazyLoad height={152} offset={333} once scrollContainer="#hotpleitems-container">
                          <img className={index === 0 ? styles.image1Icon : styles.image3Icon} alt="" src={url} />
                        </LazyLoad>
                        {hp.media_id && (
                          <div className={index === 0 ? styles.infoframe : styles.infoframe4}>
                            {hp.media_id?.[0]?.startsWith('H')
                              ? <img className={styles.iconfire2} alt="" src="images/fire.svg" />
                              : <img className={styles.iconinstagramColor} alt="" src="images/instagram.svg" />}
                            <div className={styles.username}>@{hp.media_creator}</div>
                          </div>
                        )}
                      </div>
                    ))
                  )}

                  <div className={styles.image2frame} onClick={() => openInstagramMedia(hp.media_url)}>
                    <LazyLoad height={152} offset={333} once scrollContainer="#hotpleitems-container">
                      <img className={styles.image2Icon} alt="" src={hp.preview_url || 'images/no_instagram_image.svg'} />
                    </LazyLoad>
                    {hp.media_url && (
                      <>
                        <div className={styles.infoframe2}>
                          {hp.media_id?.[0]?.startsWith('H')
                            ? <img className={styles.iconfire2} alt="" src="images/fire.svg" />
                            : <img className={styles.iconinstagramColor} alt="" src="images/instagram.svg" />}
                          <div className={styles.username}>@{hp.media_creator}</div>
                        </div>
                        <div className={styles.infos}>
                          <img className={styles.iconshare} alt="" src="images/share.svg" />
                          <div className={styles.div}>
                            <span className={styles.txt}>
                              <p className={styles.p}>게시글</p>
                              <p className={styles.p}>보러가기</p>
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <div className={styles.padding} />
                </div>

                <div className={styles.detailframe}>
                  <div className={styles.detailleft}>
                    <div className={styles.title}>
                      <div className={styles.hotplename}>{hp.name}</div>
                      {badge && <span className={styles.badgeWrapper}>{badge}</span>}
                      {hp.latitude && hp.longitude && (
                        <div className={styles.ratingframe}>
                          <img className={styles.iconfire} alt="star" src="images/my_location.svg" />
                          <div className={styles.ratingtext}>
                            {Math.trunc(haversineDistance(devicePosition, { lat: hp.latitude, long: hp.longitude }) * 10) / 10}km
                          </div>
                        </div>
                      )}
                    </div>
                    <div className={styles.tags} onTouchStart={(e)=>{e.stopPropagation();}}>
                      <div className={styles.tagplaceDetail}><div className={styles.orange}>{hp.main_category}</div></div>
                      <div className={styles.tagplaceDetail1}><div className={styles.orange}>{hp.sub_category1} ▸ {hp.sub_category2}</div></div>
                      {hp.tags && hp.tags.slice(0, 4).map((tag, index) => (
                        <div className={styles.tagplaceDetail2} key={index}><div className={styles.orange}>#{tag}</div></div>
                      ))}
                    </div>

                    {hp.comment && (
                      <div className={styles.commentframe}>
                        <div className={styles.label}>댓글</div>
                        <div className={styles.comment}>
                          {hp.taggedBy != null && (
                            <a
                              className={styles.taggedBy}
                              href={`https://instagram.com/${hp.taggedBy}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              @{hp.taggedBy}:&nbsp;
                            </a>
                          )}
                          {hp.comment.split(/(@[\w\.]+)/g).map((part, index) => {
                            if (part.startsWith('@')) {
                              const username = part.slice(1);
                              return (
                                <a key={index} className={styles.commentUser} href={`https://instagram.com/${username}`} target="_blank" rel="noopener noreferrer">
                                  {part}
                                </a>
                              );
                            }
                            return <span key={index} className={styles.span}>{part}</span>;
                          })}
                        </div>
                      </div>
                    )}

                    {hp.has_period && (
                      <div className={styles.commentframe}>
                        <div className={styles.label}>기간</div>
                        <div className={styles.period}>
                          {hp.start_date && hp.start_date.replaceAll('-', '/')} ~ {hp.end_date && hp.end_date.replaceAll('-', '/')}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.topButtonFrame}>
                <LikeButton liked={!!liked} count={likeCount ?? 0} hasPeriod={!!hp.has_period} onClick={patchIsPick} />
                <div className={styles.mapButton} onClick={whenShareButtonClicked}>
                  <img className={styles.buttonIcon2} src="images/share.svg" alt="" />
                  <div>공유</div>
                </div>
                {hp.naver_map_place_id && (
                  <div className={styles.mapButton} onClick={whenNaverButtonClicked}>
                    <img className={styles.buttonIcon} src="images/marker.svg" alt="" />
                    <div>지도</div>
                  </div>
                )}
                {analysis?.info?.naverBooking?.naverBookingUrl && (
                  <div className={styles.mapButton} onClick={() => window.open(analysis.info.naverBooking.naverBookingUrl, '_blank')}>
                    <img className={styles.buttonIcon2} src="images/calendar.svg" alt="" />
                    <div>예약</div>
                  </div>
                )}
                {(analysis?.info?.phone || analysis?.info?.virtualPhone) && (
                  <a className={styles.mapButton} href={`tel:${analysis.info.phone || analysis.info.virtualPhone}`}>
                    <img className={styles.buttonIcon2} src="images/phone.svg" alt="" />
                    <div>전화</div>
                  </a>
                )}
                {hp.media_url && (
                  <div className={styles.mapButton} onClick={() => openInstagramMedia(hp.media_url)}>
                    <img className={styles.buttonIcon2} src="images/instagram.svg" alt="" />
                    <div>원본 게시글</div>
                  </div>
                )}
              </div>

              <div ref={sentinelRef} style={{ height: '1px' }} />

              <div className={styles.tabBartop} ref={tabRef}>
                <div className={activeTab === 'home' ? styles.list : styles.list1} onClick={() => setActiveTab('home')}>
                  <div className={activeTab === 'home' ? styles.text : styles.text1}>홈</div>
                </div>
                <div className={activeTab === 'story' ? styles.list : styles.list1} onClick={() => setActiveTab('story')}>
                  <div className={activeTab === 'story' ? styles.text : styles.text1}>스토리</div>
                </div>
                <div className={activeTab === 'photo' ? styles.list : styles.list1} onClick={() => setActiveTab('photo')}>
                  <div className={activeTab === 'photo' ? styles.text : styles.text1}>사진</div>
                </div>
              </div>

              {activeTab === 'home' && (
                <div className={styles.tabsFrame}>
                  <HotpleHomeTab hotple={hp} analysis={analysis} setHotpleModalPage={setHotpleModalPage} setModalProps={setModalProps} marginTop={marginTop}/>
                </div>
              )}

              {(activeTab === 'story' || activeTab === 'photo') && (
                <div className={styles.div2}>
                  <div className={styles.div1}>
                    <p className={styles.p}>해당 기능 제공을 위해 준비 중 이에요!</p>
                    <p className={styles.p}>조만간 업데이트 해드릴게요 :)</p>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HotpleDetailPage;
